import "./_style.less";
import { Col, Collapse, Divider, Radio, Row, Space, Tooltip } from "antd";
import { InfoCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import { ArrowDownIcon } from "../../../../components";
import CheckCircleFilledIcon from "../../../../components/icons/CheckCircleFilled";
import { formatDate, sortScheduleTimeOptions } from "../../../../@vendor/utils";
import { useTranslation } from "react-i18next";
import { useBookingCounseling } from "../../providers/booking-counseling.provider";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Schedule, ScheduleTime } from "../../../../models/Schedule";
import { isArray } from "lodash";

interface IBookingCounselingMultipleSession {
  schedules: Schedule[];
  activeIndex?: number;
}

const BookingCounselingMultipleSession = ({
  schedules,
  activeIndex,
}: IBookingCounselingMultipleSession) => {
  const { t } = useTranslation();
  const { form } = useBookingCounseling();
  const { values, setFieldValue } = form;
  const [sessionActiveKey, setSessionActiveKey] = useState<any>([]);
  const [incompletePanels, setIncompletePanels] = useState<
    Record<number, boolean>
  >({});

  const makeHandleCollapseChange =
    (sessionIdx: number) => (key: string | string[]) => {
      // const isDateSelected = values?.schedule_ids?.[sessionIdx] !== undefined;
      // const isTimeSelected =
      //   Array.isArray(values.time_ids) && !!values.time_ids[sessionIdx];
      // if (!isDateSelected) return;
      // const keyArr = Array.isArray(key)
      //   ? key.map((k) => Number(k))
      //   : [Number(key)];
      // const activeArr = Array.isArray(sessionActiveKey)
      //   ? sessionActiveKey.map((k) => Number(k))
      //   : [Number(sessionActiveKey)];
      // if (activeArr.includes(sessionIdx) && !keyArr.includes(sessionIdx)) {
      //   setIncompletePanels((prev) => ({
      //     ...prev,
      //     [sessionIdx]: !isTimeSelected,
      //   }));
      // }

      setSessionActiveKey(key as any);
    };

  const makeHandleMultiSessionTimeChange = (sessionIdx: number) => (e: any) => {
    const newTimeIds = Array.isArray(values?.time_ids)
      ? [...values.time_ids]
      : Array(sessionCount).fill(undefined);
    newTimeIds[sessionIdx] = e.target.value;
    setFieldValue("time_ids", newTimeIds);

    const scheduleId = values.schedule_ids[sessionIdx];
    const idx = schedules.findIndex((sc) => sc.id === scheduleId);
    const selectedTime = ((schedules[idx]?.times as ScheduleTime[]) ?? []).find(
      (item) => item.id === e.target.value
    );
    const newTimes = Array.isArray(values?.time)
      ? [...values.time]
      : Array(sessionCount).fill(undefined);
    newTimes[sessionIdx] = selectedTime;
    setFieldValue("time", newTimes);

    setIncompletePanels((prev) => ({ ...prev, [sessionIdx]: false }));
    const nextIdx = sessionIdx + 1;
    if (nextIdx < sessionCount) setSessionActiveKey(nextIdx.toString());
    else setSessionActiveKey(undefined);
  };

  const onResetAll = () => {
    setFieldValue("schedules", []);
    setFieldValue("schedule_ids", []);
    setFieldValue("time_ids", []);
    setSessionActiveKey([]);
  };

  const session = useMemo(() => {
    if (values?.session === "five-session") return 5;
    return 1;
  }, [values?.session]);

  const sessionCount = useMemo(() => {
    return values?.schedule_ids?.length ?? 0;
  }, [values?.schedule_ids]);

  useEffect(() => {
    if (activeIndex !== undefined) {
      setSessionActiveKey([activeIndex]);
    } else {
      setSessionActiveKey([]);
    }
  }, [activeIndex]);

  const checkIncompleteSession = useCallback(() => {
    let cloneIncompletePanels = {};
    if (values.schedule_ids?.length) {
      values.schedule_ids?.forEach((_: string, index: number) => {
        const isTimeSelected =
          Array.isArray(values.time_ids) && !!values.time_ids[index];
        cloneIncompletePanels = {
          ...cloneIncompletePanels,
          [index]: !isTimeSelected,
        };
      });
      setIncompletePanels(cloneIncompletePanels);
    } else {
      setIncompletePanels([]);
    }
  }, [values, sessionActiveKey, incompletePanels]);

  useEffect(() => {
    checkIncompleteSession();
  }, [sessionActiveKey]);

  return (
    <Row gutter={[15, 15]} className="booking-counseling-multiple-session">
      <Col xs={24}>
        <Row
          className="select-times-header"
          align="middle"
          justify="space-between"
          style={{ width: "100%" }}
        >
          <Col>
            <label className="schedule-section-title">
              {t("common.text.selectTimes")}
            </label>
            <div style={{ color: "#888E95", fontSize: 14 }}>
              {t("common.text.multipleSession60Minutes")}
            </div>
          </Col>
          <Col>
            <button className="plain-btn" onClick={onResetAll}>
              <Space size={4}>
                <ReloadOutlined style={{ fontSize: 14 }} />
                {t("common.text.resetAll")}
              </Space>
            </button>
          </Col>
        </Row>
      </Col>

      <Col xs={24}>
        <Space direction="vertical" size={20} style={{ width: "100%" }}>
          {Array.from({ length: session }).map((_, sessionIdx) => {
            const isDateSelected =
              values?.schedule_ids?.[sessionIdx] !== undefined;
            const isTimeSelected =
              Array.isArray(values.time_ids) && !!values.time_ids[sessionIdx];
            const isIncomplete = incompletePanels[sessionIdx];
            const collapseClass = isIncomplete ? "ant-collapse-incomplete" : "";

            return (
              <Collapse
                key={sessionIdx}
                activeKey={sessionActiveKey}
                onChange={makeHandleCollapseChange(sessionIdx)}
                expandIconPosition="end"
                className={collapseClass}
                expandIcon={({ isActive }) => (
                  <ArrowDownIcon
                    className="collapse-arrow-icon"
                    style={{
                      transform: isActive
                        ? "rotate(180deg),translateY(-50%)"
                        : "rotate(0deg),translateY(-50%)",
                    }}
                  />
                )}
              >
                <Collapse.Panel
                  disabled={!isDateSelected}
                  key={sessionIdx}
                  showArrow
                  header={
                    <>
                      <div id={`counseling-session-${sessionIdx}`} />
                      <Row className={"collapse-panel-header-row"}>
                        <Col>
                          <Row
                            className="collapse-panel-header-info"
                            gutter={12}
                          >
                            <Col
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 6,
                              }}
                            >
                              <span className="collapse-panel-header-session">{`Session ${
                                sessionIdx + 1
                              }`}</span>
                              {isTimeSelected ? (
                                <CheckCircleFilledIcon />
                              ) : isIncomplete ? (
                                <Tooltip
                                  title={t("common.text.pleaseSelectTime")}
                                >
                                  <InfoCircleOutlined
                                    style={{
                                      color: "#FF290B",
                                      fontSize: 18,
                                    }}
                                  />
                                </Tooltip>
                              ) : null}
                            </Col>
                            <Col>
                              <Divider
                                type="vertical"
                                className="collapse-panel-header-divider"
                              />
                            </Col>
                            <Col className="collapse-panel-header-date">
                              {isDateSelected
                                ? (() => {
                                    const scheduleId =
                                      values.schedule_ids[sessionIdx];
                                    const schedule = schedules.find(
                                      (sc) => sc.id === scheduleId
                                    );
                                    let timeLabel = "";
                                    if (
                                      Array.isArray(values.time_ids) &&
                                      values.time_ids[sessionIdx]
                                    ) {
                                      const timeId =
                                        values.time_ids[sessionIdx];
                                      const idx = schedules.findIndex(
                                        (sc) => sc.id === scheduleId
                                      );
                                      const timeOption =
                                        sortScheduleTimeOptions(
                                          (schedules[idx]
                                            ?.times as ScheduleTime[]) ?? []
                                        ).find((item) => item.id === timeId);
                                      if (timeOption)
                                        timeLabel = timeOption.label;
                                    }
                                    if (!schedule?.date)
                                      return "Please select a date";
                                    return `${formatDate(schedule.date)}${
                                      timeLabel
                                        ? " - " + timeLabel + " WIB"
                                        : ""
                                    }`;
                                  })()
                                : "Please select a date"}
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </>
                  }
                >
                  <Radio.Group
                    value={
                      Array.isArray(values?.time_ids)
                        ? values.time_ids[sessionIdx]
                        : undefined
                    }
                    className="counseling-time-select"
                    onChange={makeHandleMultiSessionTimeChange(sessionIdx)}
                  >
                    {isDateSelected &&
                      (() => {
                        const scheduleId = values.schedule_ids[sessionIdx];
                        const idx = schedules.findIndex(
                          (sc) => sc.id === scheduleId
                        );
                        return sortScheduleTimeOptions(
                          (schedules[idx]?.times as ScheduleTime[]) ?? []
                        ).map((item) => (
                          <Radio.Button
                            key={item.id}
                            value={item.id}
                            disabled={!item.is_available_in_sixty_minutes}
                          >
                            {formatDate(item.timestamp, "HH:mm")}
                          </Radio.Button>
                        ));
                      })()}
                  </Radio.Group>
                </Collapse.Panel>
              </Collapse>
            );
          })}
        </Space>
      </Col>
    </Row>
  );
};

export default BookingCounselingMultipleSession;
